import { useEffect } from 'react';

export const PAGE_SIZE_OPTIONS = [10, 20, 50, 100] as const;
export type PageSizeOption = typeof PAGE_SIZE_OPTIONS[number];

interface PaginationProps {
  currentPage: number;
  totalItems: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  itemLabel?: string; // e.g. "registo(s)" or "funcionário(s)"
}

function buildPageList(current: number, total: number): (number | 'ellipsis')[] {
  return Array.from({ length: total }, (_, i) => i + 1)
    .filter((p) => p === 1 || p === total || Math.abs(p - current) <= 2)
    .reduce<(number | 'ellipsis')[]>((acc, p, idx, arr) => {
      if (idx > 0 && p - (arr[idx - 1] as number) > 1) acc.push('ellipsis');
      acc.push(p);
      return acc;
    }, []);
}

export default function Pagination({
  currentPage,
  totalItems,
  pageSize,
  onPageChange,
  onPageSizeChange,
  itemLabel = 'item(s)',
}: PaginationProps) {
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const safePage = Math.min(currentPage, totalPages);

  // Auto-correct if current page is out of range
  useEffect(() => {
    if (currentPage > totalPages) onPageChange(totalPages);
  }, [currentPage, totalPages, onPageChange]);

  if (totalItems === 0) return null;

  const from = (safePage - 1) * pageSize + 1;
  const to = Math.min(safePage * pageSize, totalItems);
  const pages = buildPageList(safePage, totalPages);

  return (
    <div className="flex items-center justify-between bg-white rounded-xl border border-zinc-100 px-5 py-3">
      {/* Left: info + page size selector */}
      <div className="flex items-center gap-3">
        <span className="text-[12px] text-zinc-400">
          <span className="font-semibold text-zinc-600">{from}–{to}</span>
          {' '}de{' '}
          <span className="font-semibold text-zinc-600">{totalItems}</span>
          {' '}{itemLabel}
        </span>
        <div className="flex items-center gap-1.5">
          <span className="text-[11px] text-zinc-400">Mostrar</span>
          <div className="flex items-center gap-0.5 bg-zinc-100 rounded-lg p-0.5">
            {PAGE_SIZE_OPTIONS.map((size) => (
              <button
                key={size}
                onClick={() => { onPageSizeChange(size); onPageChange(1); }}
                className={`whitespace-nowrap px-2.5 py-1 rounded-md text-[11px] font-medium transition-colors cursor-pointer ${
                  pageSize === size
                    ? 'bg-white text-zinc-900'
                    : 'text-zinc-500 hover:text-zinc-700'
                }`}
              >
                {size}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Right: page navigation (only if more than 1 page) */}
      {totalPages > 1 && (
        <div className="flex items-center gap-1">
          <button
            onClick={() => onPageChange(1)}
            disabled={safePage === 1}
            className="whitespace-nowrap w-8 h-8 flex items-center justify-center rounded-lg text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors cursor-pointer"
            title="Primeira página"
          >
            <i className="ri-skip-back-line text-sm" />
          </button>
          <button
            onClick={() => onPageChange(Math.max(1, safePage - 1))}
            disabled={safePage === 1}
            className="whitespace-nowrap w-8 h-8 flex items-center justify-center rounded-lg text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors cursor-pointer"
            title="Página anterior"
          >
            <i className="ri-arrow-left-s-line text-sm" />
          </button>

          {pages.map((item, idx) =>
            item === 'ellipsis' ? (
              <span key={`e-${idx}`} className="w-8 h-8 flex items-center justify-center text-zinc-300 text-xs">…</span>
            ) : (
              <button
                key={item}
                onClick={() => onPageChange(item as number)}
                className={`whitespace-nowrap w-8 h-8 flex items-center justify-center rounded-lg text-[12px] font-medium transition-colors cursor-pointer ${
                  safePage === item
                    ? 'bg-zinc-900 text-white'
                    : 'text-zinc-500 hover:bg-zinc-100 hover:text-zinc-700'
                }`}
              >
                {item}
              </button>
            )
          )}

          <button
            onClick={() => onPageChange(Math.min(totalPages, safePage + 1))}
            disabled={safePage === totalPages}
            className="whitespace-nowrap w-8 h-8 flex items-center justify-center rounded-lg text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors cursor-pointer"
            title="Próxima página"
          >
            <i className="ri-arrow-right-s-line text-sm" />
          </button>
          <button
            onClick={() => onPageChange(totalPages)}
            disabled={safePage === totalPages}
            className="whitespace-nowrap w-8 h-8 flex items-center justify-center rounded-lg text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors cursor-pointer"
            title="Última página"
          >
            <i className="ri-skip-forward-line text-sm" />
          </button>
        </div>
      )}
    </div>
  );
}
